import { useState, useRef, useEffect } from 'react'
import { insertImageAtCursor, uploadImageFile } from '@/lib/image-upload'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workspaceApi, taskApi, pipelineApi, projectApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useToast } from '@/stores/toast-store'
import { PriorityBadge } from '@/components/ui/priority-badge'
import { StatusBadge } from '@/components/ui/status-badge'
import { Skeleton } from '@/components/ui/skeleton'
import { getActiveModuleDefinitions } from '@/lib/module-registry'
import type { TaskPriority, ModuleId, ModuleDataMap } from '@/types/task'
import { ChevronDown, Calendar, User, Check } from 'lucide-react'
import * as Popover from '@radix-ui/react-popover'

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Mendesak' },
  { value: 'high', label: 'Tinggi' },
  { value: 'medium', label: 'Sedang' },
  { value: 'low', label: 'Rendah' },
]

function getDueDateClass(dueDate: string | null): string {
  if (!dueDate) return 'text-muted-foreground'
  const today = new Date().toISOString().slice(0, 10)
  if (dueDate < today) return 'text-red-600 font-medium'
  if (dueDate === today) return 'text-orange-500 font-medium'
  return 'text-muted-foreground'
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatDateShort(date: string): string {
  return new Date(date).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
  })
}

function formatRelativeDate(date: string): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (diff === 0) return 'Hari ini'
  if (diff === 1) return 'Besok'
  if (diff === -1) return 'Kemarin'
  if (diff > 1 && diff <= 7) return `${diff} hari lagi`
  if (diff < -1 && diff >= -7) return `${Math.abs(diff)} hari lalu`
  return formatDate(date)
}

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  urgent: 'Mendesak',
  high: 'Tinggi',
  medium: 'Sedang',
  low: 'Rendah',
}

function PriorityIcon({ priority }: { priority: TaskPriority }) {
  const cfg: Record<TaskPriority, { ch: string; cls: string }> = {
    urgent: { ch: '!', cls: 'bg-red-500 text-white' },
    high:   { ch: '↑', cls: 'bg-orange-500 text-white' },
    medium: { ch: '–', cls: 'bg-blue-500 text-white' },
    low:    { ch: '↓', cls: 'bg-muted text-muted-foreground' },
  }
  const { ch, cls } = cfg[priority]
  return (
    <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] font-bold ${cls}`}>
      {ch}
    </span>
  )
}

// ── Markdown utilities ────────────────────────────────────────────────────────

/** Apply or replace a block (heading) prefix on the line containing `start`. */
function applyBlockFormat(
  value: string,
  start: number,
  end: number,
  prefix: string,
): { newValue: string; newStart: number; newEnd: number } {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1
  const lineEnd = value.indexOf('\n', start)
  const line = value.slice(lineStart, lineEnd === -1 ? undefined : lineEnd)
  const stripped = line.replace(/^#{1,4} /, '')
  const newLine = prefix + stripped
  const delta = newLine.length - line.length
  const newValue =
    value.slice(0, lineStart) +
    newLine +
    (lineEnd === -1 ? '' : value.slice(lineEnd))
  return {
    newValue,
    newStart: Math.max(lineStart, start + delta),
    newEnd: end + delta,
  }
}

/** Wrap or unwrap selected text with an inline `wrapper` string (e.g. `**` or `*`). */
function applyInlineFormat(
  value: string,
  start: number,
  end: number,
  wrapper: string,
): { newValue: string; newStart: number; newEnd: number } {
  const selected = value.slice(start, end)
  const wl = wrapper.length
  // Check if already wrapped
  if (
    value.slice(start - wl, start) === wrapper &&
    value.slice(end, end + wl) === wrapper
  ) {
    // Unwrap
    const newValue = value.slice(0, start - wl) + selected + value.slice(end + wl)
    return { newValue, newStart: start - wl, newEnd: end - wl }
  }
  // Wrap
  const newValue = value.slice(0, start) + wrapper + selected + wrapper + value.slice(end)
  return { newValue, newStart: start + wl, newEnd: end + wl }
}

/** Replace selected text with `[selected](url)`. */
function applyLink(
  value: string,
  start: number,
  end: number,
  url: string,
): { newValue: string; newCursor: number } {
  const selected = value.slice(start, end) || 'link'
  const insertion = `[${selected}](${url})`
  const newValue = value.slice(0, start) + insertion + value.slice(end)
  return { newValue, newCursor: start + insertion.length }
}

/** Wrap selected text in a fenced code block: ```\nselected\n```. */
function applyCodeBlock(
  value: string,
  start: number,
  end: number,
): { newValue: string; newStart: number; newEnd: number } {
  const selected = value.slice(start, end)
  const insertion = '```\n' + selected + '\n```'
  const newValue = value.slice(0, start) + insertion + value.slice(end)
  const newStart = start + 4 // skip "```\n"
  const newEnd = newStart + selected.length
  return { newValue, newStart, newEnd }
}

type ListType = 'ul' | 'ol' | 'cl'

function getListType(line: string): ListType | null {
  if (/^- \[[ x]\] /.test(line)) return 'cl'
  if (/^- /.test(line)) return 'ul'
  if (/^\d+\. /.test(line)) return 'ol'
  return null
}

function stripListPrefix(line: string): string {
  return line.replace(/^(\d+\. |- \[[ x]\] |- )/, '')
}

/** Apply or remove a list prefix on every line touched by the selection. Toggle when all lines already match. */
function applyListFormat(
  value: string,
  start: number,
  end: number,
  type: ListType,
): { newValue: string; newStart: number; newEnd: number } {
  const blockStart = value.lastIndexOf('\n', start - 1) + 1
  const rawBlockEnd = value.indexOf('\n', end)
  const blockEnd = rawBlockEnd === -1 ? value.length : rawBlockEnd
  const block = value.slice(blockStart, blockEnd)
  const lines = block.split('\n')

  const allMatch = lines.every((l) => getListType(l) === type)
  let num = 1
  const newLines = allMatch
    ? lines.map(stripListPrefix)
    : lines.map((l) => {
        const stripped = stripListPrefix(l)
        if (type === 'ul') return '- ' + stripped
        if (type === 'ol') return `${num++}. ` + stripped
        return '- [ ] ' + stripped
      })

  const newBlock = newLines.join('\n')
  const firstLineDelta = newLines[0].length - lines[0].length
  const totalDelta = newBlock.length - block.length
  const newValue = value.slice(0, blockStart) + newBlock + value.slice(blockEnd)
  return {
    newValue,
    newStart: Math.max(blockStart, start + firstLineDelta),
    newEnd: end + totalDelta,
  }
}

/** Toggle a checklist item at `lineIdx` between `- [ ] ` and `- [x] `. */
function applyCheckboxToggle(value: string, lineIdx: number): string {
  const lines = value.split('\n')
  if (lineIdx < 0 || lineIdx >= lines.length) return value
  const line = lines[lineIdx]
  if (line.startsWith('- [ ] ')) lines[lineIdx] = '- [x] ' + line.slice(6)
  else if (line.startsWith('- [x] ')) lines[lineIdx] = '- [ ] ' + line.slice(6)
  return lines.join('\n')
}

// ── SimpleMarkdown renderer ───────────────────────────────────────────────────

function renderInline(text: string): React.ReactNode[] {
  // Pattern: **bold**, `code`, *italic*, ![alt](url), [text](url) — processed in order
  // Image must come before link so the leading ! is consumed first
  const parts: React.ReactNode[] = []
  const re = /(\*\*(.+?)\*\*)|(`([^`]+)`)|(\*(.+?)\*)|(!(\[(.*?)\]\((.+?)\)))|(\[(.+?)\]\((.+?)\))/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    if (match[1]) {
      parts.push(<strong key={key++}>{match[2]}</strong>)
    } else if (match[3]) {
      parts.push(
        <code key={key++} className="rounded bg-secondary px-1 font-mono text-[0.8em]">
          {match[4]}
        </code>
      )
    } else if (match[5]) {
      parts.push(<em key={key++}>{match[6]}</em>)
    } else if (match[7]) {
      // Image: match[9]=alt, match[10]=url
      parts.push(
        <img key={key++} src={match[10]} alt={match[9] || ''} loading="lazy"
          className="max-w-full rounded-md my-1" />
      )
    } else if (match[11]) {
      // Link: match[12]=text, match[13]=url
      parts.push(
        <a key={key++} href={match[13]} target="_blank" rel="noopener noreferrer"
          className="text-primary hover:underline">
          {match[12]}
        </a>
      )
    }
    lastIndex = re.lastIndex
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return parts
}

function SimpleMarkdown({
  text,
  onCheckboxToggle,
}: {
  text: string
  onCheckboxToggle?: (lineIdx: number) => void
}) {
  const lines = text.split('\n')
  const nodes: React.ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]

    // Fenced code block
    if (line.startsWith('```')) {
      const codeLines: string[] = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      nodes.push(
        <pre key={i} className="rounded-xl border border-border bg-secondary p-3 font-mono text-xs overflow-x-auto my-1">
          <code>{codeLines.join('\n')}</code>
        </pre>
      )
      i++ // skip closing ```
      continue
    }

    // List groups
    const lt = getListType(line)
    if (lt) {
      const groupStart = i
      const items: { text: string; checked: boolean }[] = []
      while (i < lines.length && getListType(lines[i]) === lt) {
        const l = lines[i]
        if (lt === 'cl') {
          items.push({ text: l.slice(6), checked: l.startsWith('- [x] ') })
        } else if (lt === 'ul') {
          items.push({ text: l.slice(2), checked: false })
        } else {
          items.push({ text: l.replace(/^\d+\. /, ''), checked: false })
        }
        i++
      }

      if (lt === 'cl') {
        nodes.push(
          <ul key={groupStart} className="space-y-1 my-1 list-none pl-0">
            {items.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => onCheckboxToggle?.(groupStart + idx)}
                  className={`mt-0.5 shrink-0 text-base leading-none text-muted-foreground hover:text-foreground ${onCheckboxToggle ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {item.checked ? '☑' : '☐'}
                </button>
                <span className={item.checked ? 'line-through text-muted-foreground' : ''}>
                  {renderInline(item.text)}
                </span>
              </li>
            ))}
          </ul>
        )
      } else if (lt === 'ul') {
        nodes.push(
          <ul key={groupStart} className="list-disc pl-5 space-y-0.5 my-1">
            {items.map((item, idx) => <li key={idx}>{renderInline(item.text)}</li>)}
          </ul>
        )
      } else {
        nodes.push(
          <ol key={groupStart} className="list-decimal pl-5 space-y-0.5 my-1">
            {items.map((item, idx) => <li key={idx}>{renderInline(item.text)}</li>)}
          </ol>
        )
      }
      continue
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line.trim())) {
      nodes.push(<hr key={i} className="border-border my-3" />)
      i++
      continue
    }

    // Table
    if (line.startsWith('|')) {
      const groupStart = i
      const tableLines: string[] = []
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i])
        i++
      }
      const parseRow = (l: string) => l.split('|').slice(1, -1).map((c) => c.trim())
      const isSep = (l: string) => parseRow(l).every((c) => /^[\s\-:]+$/.test(c))
      const sepIdx = tableLines.findIndex((l, idx) => idx > 0 && isSep(l))
      if (sepIdx > 0) {
        const headers = parseRow(tableLines[0])
        const bodyRows = tableLines.slice(sepIdx + 1)
        nodes.push(
          <div key={groupStart} className="overflow-x-auto my-2">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi} className="border border-border bg-secondary px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                      {renderInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => (
                  <tr key={ri} className="hover:bg-secondary/40">
                    {parseRow(row).map((cell, ci) => (
                      <td key={ci} className="border border-border px-3 py-1.5 align-top">
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      } else {
        tableLines.forEach((l, li) => nodes.push(<p key={groupStart + li}>{renderInline(l)}</p>))
      }
      continue
    }

    // Headings, blank lines, paragraphs
    if (line.startsWith('#### '))      nodes.push(<h4 key={i} className="text-sm font-semibold">{renderInline(line.slice(5))}</h4>)
    else if (line.startsWith('### '))  nodes.push(<h3 key={i} className="text-base font-semibold">{renderInline(line.slice(4))}</h3>)
    else if (line.startsWith('## '))   nodes.push(<h2 key={i} className="text-lg font-semibold">{renderInline(line.slice(3))}</h2>)
    else if (line.startsWith('# '))    nodes.push(<h1 key={i} className="text-xl font-bold">{renderInline(line.slice(2))}</h1>)
    else if (line === '')              nodes.push(<div key={i} className="h-2" />)
    else                              nodes.push(<p key={i}>{renderInline(line)}</p>)
    i++
  }
  return <div className="space-y-1">{nodes}</div>
}

export default function TaskDetailPage() {
  const { slug, projectId, taskId } = useParams<{ slug: string; projectId: string; taskId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((s) => s.user?.id)
  const { toast } = useToast()

  const [isEditing, setIsEditing] = useState(false)
  const [deletingConfirm, setDeletingConfirm] = useState(false)

  // Right panel collapsible sections
  const [propsOpen, setPropsOpen] = useState(true)
  const [labelsOpen, setLabelsOpen] = useState(true)
  const [projectOpen, setProjectOpen] = useState(true)

  // Quick-change popover state
  const [statusOpen, setStatusOpen] = useState(false)
  const [statusSearch, setStatusSearch] = useState('')
  const [priorityOpen, setPriorityOpen] = useState(false)
  const [assigneeOpen, setAssigneeOpen] = useState(false)
  const [dueDateOpen, setDueDateOpen] = useState(false)

  // Edit form state — initialised when entering edit mode
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editStatusId, setEditStatusId] = useState('')
  const [editPriority, setEditPriority] = useState<TaskPriority>('medium')
  const [editDueDate, setEditDueDate] = useState('')
  const [editAssignedTo, setEditAssignedTo] = useState('')
  const [editAssetUrl, setEditAssetUrl] = useState('')
  const [editModuleData, setEditModuleData] = useState<ModuleDataMap>({})

  // Auto-resize description textarea to content height
  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = descriptionRef.current
    if (!el || !isEditing) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [editDescription, isEditing])

  // Selection toolbar state
  const [selToolbar, setSelToolbar] = useState<{ x: number; y: number } | null>(null)
  const [selRange, setSelRange] = useState<{ start: number; end: number } | null>(null)
  const [linkMode, setLinkMode] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const toolbarRef = useRef<HTMLDivElement>(null)

  // Image upload state
  const [imgUploading, setImgUploading] = useState(false)
  const imgFileInputRef = useRef<HTMLInputElement>(null)
  const savedCursorRef = useRef<number | null>(null)

  function handleDescMouseUp(e: React.MouseEvent<HTMLTextAreaElement>) {
    const el = e.currentTarget
    const { selectionStart: s, selectionEnd: end } = el
    if (s === end) { setSelToolbar(null); setSelRange(null); return }
    setSelRange({ start: s, end })
    setSelToolbar({ x: e.clientX, y: e.clientY })
  }

  function handleDescKeyUp(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    const el = e.currentTarget
    const { selectionStart: s, selectionEnd: end } = el
    if (s === end) { setSelToolbar(null); setSelRange(null); return }
    setSelRange({ start: s, end })
    const rect = el.getBoundingClientRect()
    setSelToolbar({ x: rect.left + rect.width / 2, y: rect.top })
  }

  function applyFormat(type: 'regular' | 'h1' | 'h2' | 'h3' | 'h4' | 'bold' | 'italic' | 'code') {
    if (!selRange) return
    const { start, end } = selRange
    let result: { newValue: string; newStart: number; newEnd: number }
    if (['regular', 'h1', 'h2', 'h3', 'h4'].includes(type)) {
      const level = type === 'regular' ? 0 : Number(type[1])
      const prefix = level === 0 ? '' : '#'.repeat(level) + ' '
      result = applyBlockFormat(editDescription, start, end, prefix)
    } else {
      const wrapper = type === 'bold' ? '**' : type === 'code' ? '`' : '*'
      result = applyInlineFormat(editDescription, start, end, wrapper)
    }
    setEditDescription(result.newValue)
    setSelRange({ start: result.newStart, end: result.newEnd })
    requestAnimationFrame(() => {
      const el = descriptionRef.current
      if (!el) return
      el.setSelectionRange(result.newStart, result.newEnd)
      el.focus()
    })
  }

  function applyLinkFormat() {
    if (!selRange || !linkUrl) return
    const result = applyLink(editDescription, selRange.start, selRange.end, linkUrl)
    setEditDescription(result.newValue)
    setSelToolbar(null); setSelRange(null); setLinkMode(false); setLinkUrl('')
    requestAnimationFrame(() => {
      const el = descriptionRef.current
      if (el) { el.setSelectionRange(result.newCursor, result.newCursor); el.focus() }
    })
  }

  function applyCodeBlockFormat() {
    if (!selRange) return
    const result = applyCodeBlock(editDescription, selRange.start, selRange.end)
    setEditDescription(result.newValue)
    setSelRange({ start: result.newStart, end: result.newEnd })
    requestAnimationFrame(() => {
      const el = descriptionRef.current
      if (!el) return
      el.setSelectionRange(result.newStart, result.newEnd)
      el.focus()
    })
  }

  function applyListHandler(type: ListType) {
    if (!selRange) return
    const result = applyListFormat(editDescription, selRange.start, selRange.end, type)
    setEditDescription(result.newValue)
    setSelRange({ start: result.newStart, end: result.newEnd })
    requestAnimationFrame(() => {
      const el = descriptionRef.current
      if (!el) return
      el.setSelectionRange(result.newStart, result.newEnd)
      el.focus()
    })
  }

  async function handleImageUpload(file: File) {
    setImgUploading(true)
    try {
      const url = await uploadImageFile(file)
      if (descriptionRef.current) {
        insertImageAtCursor(descriptionRef.current, url, setEditDescription, savedCursorRef.current ?? undefined)
      }
    } catch (err) {
      toast({
        title: 'Gagal mengunggah gambar',
        description: err instanceof Error ? err.message : 'Upload gagal',
        variant: 'destructive',
      })
    } finally {
      setImgUploading(false)
      savedCursorRef.current = null
      if (imgFileInputRef.current) imgFileInputRef.current.value = ''
    }
  }

  const { data: members } = useQuery({
    queryKey: ['workspace', slug, 'members'],
    queryFn: () => workspaceApi.getMembers(slug!),
    enabled: !!slug,
  })

  const { data: statuses } = useQuery({
    queryKey: ['workspace', slug, 'pipeline'],
    queryFn: () => pipelineApi.getStatuses(slug!),
    enabled: !!slug,
  })

  const {
    data: task,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['workspace', slug, 'task', taskId],
    queryFn: () => taskApi.getTask(slug!, taskId!),
    enabled: !!slug && !!taskId,
  })

  // Fetch active modules for this task's project (projectId from task, not URL)
  const taskProjectId = task?.project_id
  const { data: modulesData } = useQuery({
    queryKey: ['workspace', slug, 'project', taskProjectId, 'modules'],
    queryFn: () => projectApi.getProjectModules(slug!, taskProjectId!),
    enabled: !!slug && !!taskProjectId,
    staleTime: 60_000,
  })
  const activeModules: ModuleId[] = modulesData?.active_modules ?? []

  // Derive permissions
  const myMember = members?.find((m) => m.user_id === currentUserId)
  const myRole = myMember?.role
  const isPrivileged = myRole === 'owner' || myRole === 'admin'
  const isCreator = task?.created_by === currentUserId
  const canEdit = myRole !== 'viewer' && (isPrivileged || isCreator)
  const canDelete = isPrivileged || isCreator

  const invalidateTask = () => {
    void queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'task', taskId] })
    void queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'tasks'] })
  }

  const updateMutation = useMutation({
    mutationFn: () =>
      taskApi.updateTask(slug!, taskId!, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        status_id: editStatusId || null,
        priority: editPriority,
        due_date: editDueDate || null,
        assigned_to: editAssignedTo || null,
        asset_url: editAssetUrl.trim() || null,
        module_data: activeModules.length > 0 ? editModuleData : null,
      }),
    onSuccess: () => {
      invalidateTask()
      setIsEditing(false)
      toast({ title: 'Tugas berhasil diperbarui' })
    },
    onError: (err) => {
      toast({ title: 'Gagal memperbarui tugas', description: (err as Error).message, variant: 'destructive' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => taskApi.deleteTask(slug!, taskId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'project', projectId, 'tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] })
      toast({ title: 'Tugas berhasil dihapus' })
      navigate(`/workspaces/${slug}/projects/${projectId}`, { replace: true })
    },
    onError: (err) => {
      toast({ title: 'Gagal menghapus tugas', description: (err as Error).message, variant: 'destructive' })
    },
  })

  const checkboxMutation = useMutation({
    mutationFn: (newDescription: string) =>
      taskApi.updateTask(slug!, taskId!, {
        title: task!.title,
        description: newDescription,
        status_id: task!.status_id ?? null,
        priority: task!.priority,
        due_date: task!.due_date ?? null,
        assigned_to: task!.assigned_to ?? null,
        asset_url: task!.asset_url ?? null,
        module_data: task!.module_data ?? null,
      }),
    onSuccess: () => invalidateTask(),
  })

  const quickMutation = useMutation({
    mutationFn: (data: { status_id?: string | null; priority?: TaskPriority; assigned_to?: string | null; due_date?: string | null }) =>
      taskApi.updateTask(slug!, taskId!, data),
    onSuccess: () => invalidateTask(),
    onError: (err) => {
      toast({ title: 'Gagal memperbarui', description: (err as Error).message, variant: 'destructive' })
    },
  })

  function enterEditMode() {
    if (!task) return
    setEditTitle(task.title)
    setEditDescription(task.description ?? '')
    setEditStatusId(task.status_id ?? '')
    setEditPriority(task.priority)
    setEditDueDate(task.due_date ?? '')
    setEditAssignedTo(task.assigned_to ?? '')
    setEditAssetUrl(task.asset_url ?? '')
    setEditModuleData(task.module_data ?? {})
    setIsEditing(true)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-32" />
      </div>
    )
  }

  if (error || !task) {
    return (
      <p className="text-sm text-destructive">Tugas tidak ditemukan atau kamu tidak punya akses.</p>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground overflow-hidden">
        <Link to={`/workspaces/${slug}/projects`} className="shrink-0 hover:text-foreground">
          Projects
        </Link>
        <span className="shrink-0">/</span>
        <Link
          to={`/workspaces/${slug}/projects/${projectId}`}
          className="truncate hover:text-foreground max-w-[160px]"
        >
          {task.project_name}
        </Link>
        <span className="shrink-0">/</span>
        <span className="truncate text-foreground font-medium max-w-[200px]">
          {task.title}
        </span>
      </nav>

      {isEditing ? (
        /* ── Edit mode — two-column ──────────────────────────────────────── */
        <form
          onSubmit={(e) => {
            e.preventDefault()
            updateMutation.mutate()
          }}
          className="flex flex-col md:flex-row gap-8 items-start"
        >
          {/* Left — content */}
          <div className="flex-1 min-w-0 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Judul <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                required
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Deskripsi</label>
              <input
                ref={imgFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImageUpload(f) }}
              />
              <textarea
                ref={descriptionRef}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                onMouseUp={handleDescMouseUp}
                onKeyUp={handleDescKeyUp}
                onPaste={(e) => {
                  const imgItem = Array.from(e.clipboardData?.items ?? []).find((it) => it.type.startsWith('image/'))
                  if (imgItem) {
                    e.preventDefault()
                    const file = imgItem.getAsFile()
                    if (file) void handleImageUpload(file)
                  }
                }}
                onBlur={(e) => {
                  if (!toolbarRef.current?.contains(e.relatedTarget as Node)) {
                    setSelToolbar(null); setSelRange(null); setLinkMode(false)
                  }
                }}
                disabled={imgUploading}
                rows={3}
                className={`w-full resize-none overflow-hidden rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring${imgUploading ? ' opacity-50 cursor-wait' : ''}`}
              />
            </div>

            {/* Module fields — edit mode */}
            {activeModules.length > 0 && (
              <div className="space-y-4 border-t border-border pt-4">
                <p className="text-sm font-medium text-muted-foreground">Modul Project</p>
                {getActiveModuleDefinitions(activeModules).map((mod) => {
                  const Comp = mod.component
                  const isVault  = mod.id === 'vault'
                  const isBranch = mod.id === 'branch'
                  return (
                    <div key={mod.id} className="space-y-1.5">
                      <label className="text-sm font-medium">
                        {mod.icon} {mod.label}
                        {isVault && (
                          <span className="ml-1.5 text-xs font-normal text-muted-foreground">(referensi project)</span>
                        )}
                      </label>
                      <Comp
                        moduleId={mod.id}
                        value={isVault ? modulesData?.module_data?.vault : editModuleData[mod.id]}
                        onChange={(val: ModuleDataMap[ModuleId]) =>
                          setEditModuleData((prev) => ({ ...prev, [mod.id]: val }))
                        }
                        readOnly={isVault}
                        {...(isBranch && { taskMode: true })}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Right — metadata + actions */}
          <div className="w-full md:w-64 shrink-0 rounded-xl border border-border bg-card p-4 space-y-4 md:sticky md:top-6">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <select
                value={editStatusId}
                onChange={(e) => setEditStatusId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Tanpa Status</option>
                {statuses?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Prioritas</label>
              <select
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value as TaskPriority)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Due Date</label>
              <input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Assignee</label>
              <select
                value={editAssignedTo}
                onChange={(e) => setEditAssignedTo(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Tidak Ada</option>
                {members?.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name ?? m.email ?? m.user_id}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Asset Link</label>
              <input
                type="url"
                value={editAssetUrl}
                onChange={(e) => setEditAssetUrl(e.target.value)}
                placeholder="https://..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Menyimpan...' : 'Simpan'}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Batal
              </button>
            </div>
          </div>
        </form>
      ) : (
        /* ── View mode — two-column ──────────────────────────────────────── */
        <>
          {/* Mobile-only property chips strip */}
          <div className="flex md:hidden overflow-x-auto gap-2 py-2 shrink-0">
            <StatusBadge name={task.status_name} color={task.status_color} />
            <PriorityBadge priority={task.priority} />
            {task.assignee_name && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                {task.assignee_avatar && (
                  <img src={task.assignee_avatar} alt="" className="h-3.5 w-3.5 rounded-full object-cover" />
                )}
                <span className="max-w-[80px] truncate">{task.assignee_name}</span>
              </span>
            )}
            {task.due_date && (
              <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs ${getDueDateClass(task.due_date)}`}>
                {formatDateShort(task.due_date)}
              </span>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">
              {canEdit && (
                <button
                  onClick={enterEditMode}
                  className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setDeletingConfirm(true)}
                  className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/10"
                >
                  Hapus
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Left — content */}
          <div className="flex-1 min-w-0 space-y-5">
            <h1 className="text-2xl font-bold tracking-tight leading-snug pl-3 border-l-2 border-primary md:pl-0 md:border-l-0">{task.title}</h1>

            {/* Description */}
            {task.description ? (
              <div className="text-sm md:rounded-xl md:border md:border-border md:bg-card md:p-4">
                <SimpleMarkdown
                  text={task.description}
                  onCheckboxToggle={(lineIdx) => {
                    const toggled = applyCheckboxToggle(task.description ?? '', lineIdx)
                    checkboxMutation.mutate(toggled)
                  }}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">Tidak ada deskripsi.</p>
            )}

            {/* Module fields — view mode */}
            {activeModules.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Modul Project</p>
                {getActiveModuleDefinitions(activeModules).map((mod) => {
                  const Comp = mod.component
                  const isVault = mod.id === 'vault'
                  return (
                    <div
                      key={mod.id}
                      className="rounded-xl border border-border bg-card px-4 py-3"
                    >
                      <p className="mb-2 text-sm text-muted-foreground">
                        {mod.icon} {mod.label}
                        {isVault && (
                          <span className="ml-1.5 text-xs">(referensi project)</span>
                        )}
                      </p>
                      <Comp
                        moduleId={mod.id}
                        value={isVault ? modulesData?.module_data?.vault : task.module_data?.[mod.id]}
                        onChange={() => {}}
                        readOnly={true}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Right — properties sidebar (desktop only) */}
          <div className="hidden md:flex md:w-64 shrink-0 flex-col gap-2 sticky top-6">

            {/* Action buttons */}
            {(canEdit || canDelete) && (
              <div className="flex gap-2">
                {canEdit && (
                  <button
                    onClick={enterEditMode}
                    className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => setDeletingConfirm(true)}
                    className="flex-1 rounded-lg border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                  >
                    Hapus
                  </button>
                )}
              </div>
            )}

            {/* Properties section */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                type="button"
                onClick={() => setPropsOpen((p) => !p)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary/50"
              >
                <span>Properties</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${propsOpen ? '' : '-rotate-90'}`} />
              </button>
              {propsOpen && (
                <div className="divide-y divide-border border-t border-border">

                  {/* Status */}
                  <Popover.Root open={statusOpen} onOpenChange={(o) => { setStatusOpen(o); if (!o) setStatusSearch('') }}>
                    <Popover.Trigger asChild>
                      <button
                        type="button"
                        disabled={!canEdit}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left disabled:cursor-default enabled:hover:bg-secondary/50"
                      >
                        <span
                          className="h-4 w-4 shrink-0 rounded-full"
                          style={{
                            backgroundColor: `${task.status_color ?? '#6b7280'}33`,
                            border: `2px solid ${task.status_color ?? '#6b7280'}`,
                          }}
                        />
                        <span className="text-sm font-medium">{task.status_name ?? 'Tanpa Status'}</span>
                      </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        side="left" align="start" sideOffset={8}
                        className="z-50 w-56 overflow-hidden rounded-xl border border-border bg-popover shadow-xl"
                      >
                        <div className="border-b border-border px-3 py-2">
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium"
                            style={{ color: task.status_color ?? '#6b7280', borderColor: task.status_color ?? '#6b7280' }}
                          >
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: task.status_color ?? '#6b7280' }} />
                            {task.status_name ?? 'Tanpa Status'}
                          </span>
                        </div>
                        <div className="border-b border-border px-2 py-1.5">
                          <input
                            autoFocus
                            placeholder="Change status..."
                            value={statusSearch}
                            onChange={(e) => setStatusSearch(e.target.value)}
                            className="w-full bg-transparent px-1 py-0.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
                          />
                        </div>
                        <div className="py-1">
                          {statuses
                            ?.filter((s) => s.name.toLowerCase().includes(statusSearch.toLowerCase()))
                            .map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => { quickMutation.mutate({ status_id: s.id }); setStatusOpen(false) }}
                                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-secondary"
                              >
                                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                                <span className="flex-1 text-left">{s.name}</span>
                                {s.id === task.status_id
                                  ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                                  : <span className="w-3.5 shrink-0 text-right text-xs text-muted-foreground">{s.position}</span>
                                }
                              </button>
                            ))}
                        </div>
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>

                  {/* Priority */}
                  <Popover.Root open={priorityOpen} onOpenChange={setPriorityOpen}>
                    <Popover.Trigger asChild>
                      <button
                        type="button"
                        disabled={!canEdit}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left disabled:cursor-default enabled:hover:bg-secondary/50"
                      >
                        <PriorityIcon priority={task.priority} />
                        <span className="text-sm font-medium">{PRIORITY_LABEL[task.priority]}</span>
                      </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        side="left" align="start" sideOffset={8}
                        className="z-50 w-44 overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-xl"
                      >
                        {PRIORITY_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { quickMutation.mutate({ priority: opt.value }); setPriorityOpen(false) }}
                            className="flex w-full items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-secondary"
                          >
                            <PriorityIcon priority={opt.value} />
                            <span className="flex-1 text-left">{opt.label}</span>
                            {opt.value === task.priority && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                          </button>
                        ))}
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>

                  {/* Assignee */}
                  <Popover.Root open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                    <Popover.Trigger asChild>
                      <button
                        type="button"
                        disabled={!canEdit}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left disabled:cursor-default enabled:hover:bg-secondary/50"
                      >
                        {task.assignee_avatar ? (
                          <img src={task.assignee_avatar} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />
                        ) : (
                          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className={`text-sm ${task.assignee_name ? 'font-medium' : 'text-muted-foreground'}`}>
                          {task.assignee_name ?? 'Tidak ada'}
                        </span>
                      </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        side="left" align="start" sideOffset={8}
                        className="z-50 w-52 overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-xl"
                      >
                        <button
                          type="button"
                          onClick={() => { quickMutation.mutate({ assigned_to: null }); setAssigneeOpen(false) }}
                          className="flex w-full items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-secondary"
                        >
                          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="flex-1 text-left text-muted-foreground">Tidak ada</span>
                          {!task.assigned_to && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                        </button>
                        {members?.map((m) => (
                          <button
                            key={m.user_id}
                            type="button"
                            onClick={() => { quickMutation.mutate({ assigned_to: m.user_id }); setAssigneeOpen(false) }}
                            className="flex w-full items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-secondary"
                          >
                            {m.avatar_url ? (
                              <img src={m.avatar_url} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" />
                            ) : (
                              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold">
                                {(m.display_name ?? m.email ?? '?')[0].toUpperCase()}
                              </span>
                            )}
                            <span className="flex-1 truncate text-left">{m.display_name ?? m.email ?? m.user_id}</span>
                            {m.user_id === task.assigned_to && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                          </button>
                        ))}
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>

                  {/* Due Date */}
                  <Popover.Root open={dueDateOpen} onOpenChange={setDueDateOpen}>
                    <Popover.Trigger asChild>
                      <button
                        type="button"
                        disabled={!canEdit}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left disabled:cursor-default enabled:hover:bg-secondary/50"
                      >
                        <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className={`text-sm ${task.due_date ? getDueDateClass(task.due_date) : 'text-muted-foreground'}`}>
                          {task.due_date ? formatRelativeDate(task.due_date) : 'Belum diatur'}
                        </span>
                      </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        side="left" align="start" sideOffset={8}
                        className="z-50 w-56 overflow-hidden rounded-xl border border-border bg-popover p-3 shadow-xl"
                      >
                        <p className="mb-2 text-xs text-muted-foreground">Due Date</p>
                        <input
                          type="date"
                          defaultValue={task.due_date ?? ''}
                          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          onChange={(e) => {
                            if (e.target.value) {
                              quickMutation.mutate({ due_date: e.target.value })
                              setDueDateOpen(false)
                            }
                          }}
                        />
                        {task.due_date && (
                          <button
                            type="button"
                            onClick={() => { quickMutation.mutate({ due_date: null }); setDueDateOpen(false) }}
                            className="mt-2 w-full rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
                          >
                            Hapus due date
                          </button>
                        )}
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>

                  {/* Asset Link */}
                  {task.asset_url && (
                    <div className="flex items-center gap-3 px-4 py-2.5">
                      <span className="text-base leading-none">🔗</span>
                      <a
                        href={task.asset_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-sm text-primary hover:underline"
                      >
                        Asset link
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Labels section */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                type="button"
                onClick={() => setLabelsOpen((p) => !p)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary/50"
              >
                <span>Labels</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${labelsOpen ? '' : '-rotate-90'}`} />
              </button>
              {labelsOpen && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-4 py-3">
                  <span className="text-xs text-muted-foreground italic">Belum ada label</span>
                </div>
              )}
            </div>

            {/* Project section */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                type="button"
                onClick={() => setProjectOpen((p) => !p)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary/50"
              >
                <span>Project</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${projectOpen ? '' : '-rotate-90'}`} />
              </button>
              {projectOpen && (
                <div className="flex items-center gap-3 border-t border-border px-4 py-2.5">
                  <span className="text-base leading-none">🎯</span>
                  <Link
                    to={`/workspaces/${slug}/projects/${task.project_id}`}
                    className="truncate text-sm font-medium hover:text-primary hover:underline"
                  >
                    {task.project_name}
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
        </>
      )}

      {/* Markdown selection toolbar — fixed overlay, only in edit mode */}
      {selToolbar && isEditing && (
        <div
          ref={toolbarRef}
          style={{
            position: 'fixed',
            top: selToolbar.y - 52,
            left: selToolbar.x,
            transform: 'translateX(-50%)',
            zIndex: 60,
          }}
          className="flex items-center gap-0.5 rounded-lg border border-border bg-card px-1 py-1 text-xs shadow-md"
          onMouseDown={(e) => e.preventDefault()}
        >
          {!linkMode ? (
            <>
              {(['regular', 'h1', 'h2', 'h3', 'h4'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => applyFormat(t)}
                  className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  {t === 'regular' ? 'T' : t.toUpperCase()}
                </button>
              ))}
              <span className="mx-0.5 h-4 w-px bg-border" />
              <button
                onClick={() => applyFormat('bold')}
                className="rounded px-1.5 py-0.5 font-bold text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                B
              </button>
              <button
                onClick={() => applyFormat('italic')}
                className="rounded px-1.5 py-0.5 italic text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                I
              </button>
              <button
                onClick={() => applyFormat('code')}
                className="rounded px-1.5 py-0.5 font-mono text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                {'\`'}
              </button>
              <button
                onClick={applyCodeBlockFormat}
                className="rounded px-1.5 py-0.5 font-mono text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                {'</>'}
              </button>
              <span className="mx-0.5 h-4 w-px bg-border" />
              <button
                onClick={() => applyListHandler('ul')}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                ≡
              </button>
              <button
                onClick={() => applyListHandler('ol')}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                1.
              </button>
              <button
                onClick={() => applyListHandler('cl')}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                ☑
              </button>
              <span className="mx-0.5 h-4 w-px bg-border" />
              <button
                onClick={() => setLinkMode(true)}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                🔗
              </button>
              <span className="mx-0.5 h-4 w-px bg-border" />
              <button
                type="button"
                disabled={imgUploading}
                onMouseDown={(e) => {
                  e.preventDefault()
                  savedCursorRef.current = descriptionRef.current?.selectionStart ?? null
                }}
                onClick={() => imgFileInputRef.current?.click()}
                title="Sisipkan gambar"
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-40"
              >
                {imgUploading
                  ? <span className="inline-block h-3 w-3 animate-spin rounded-full border border-current border-t-transparent" />
                  : '🖼'}
              </button>
            </>
          ) : (
            <>
              <input
                autoFocus
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); applyLinkFormat() }
                  if (e.key === 'Escape') setLinkMode(false)
                }}
                placeholder="https://..."
                className="w-48 rounded bg-background px-2 py-0.5 text-xs border border-input focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <button
                onClick={applyLinkFormat}
                className="rounded px-1.5 py-0.5 text-primary hover:bg-secondary"
              >
                ↵
              </button>
              <button
                onClick={() => setLinkMode(false)}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary"
              >
                ✕
              </button>
            </>
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deletingConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6">
            <h2 className="text-base font-semibold">Hapus Tugas?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Tugas <span className="font-medium">"{task.title}"</span> akan dihapus permanen.
              Tindakan ini tidak bisa dibatalkan.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeletingConfirm(false)}
                className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Batal
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="rounded bg-destructive px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
